// Mirrors @archlang/server's Workspace.buildState: take a LoadedPackage,
// run buildGraph + validate, apply cascade. Pure: no I/O.

import {
  applyCascade,
  buildGraph,
  synthesizeStubs,
  validate,
  type DependencyGraph,
  type Diagnostic,
  type ResolvedModel,
} from "@archlang/engine";

/**
 * The slice of a loaded package `buildViewerState` reads: the resolved model +
 * its diagnostics. Both this package's own `loadPackage`/`LoadedPackage` and the
 * `@archlang/lsp` package loader's output satisfy it structurally, so either
 * loader (viewer-core's Vfs path or the CLI's LSP path) can feed this builder.
 */
export interface ResolvedPackageInput {
  readonly resolved: {
    readonly model: ResolvedModel;
    readonly diagnostics: readonly Diagnostic[];
  };
}

const EMPTY_GRAPH: DependencyGraph = {
  edges: [],
  modulesByPath: new Map(),
  interfacesByPath: new Map(),
  surfacesByPath: new Map(),
  modulesById: new Map(),
  pathOfModule: new Map(),
};

const EMPTY_MODEL: ResolvedModel = {
  types: [],
  registry: { kinds: new Map(), byId: new Map() },
  modules: [],
  processes: [],
  subprocesses: [],
  views: [],
};

export interface ViewerState {
  readonly model: ResolvedModel;
  readonly rawModel: ResolvedModel;
  readonly graph: DependencyGraph;
  readonly diagnostics: readonly Diagnostic[];
}

export function buildViewerState(loaded: ResolvedPackageInput): ViewerState {
  const raw = loaded.resolved.model;
  const hasModules = raw.modules.length > 0;
  // Preserve the full model (types + registry + processes + views) even when
  // there are no module INSTANCES — a types/processes-only workspace (common
  // mid-edit) still has a registry that tooling needs. Only the per-module work
  // (cascade is a no-op over zero modules; graph has no module endpoints) is
  // gated on `hasModules`.
  // Collected DURING the cascade — it literalises field expressions, so once it
  // returns there is nothing left to fault and the colour/reference family is
  // unrecoverable. This is the cold peer of `IncrementalCompiler.state()`; both
  // have to carry it or which path built the board decides what a user is told.
  const cascadeDiags: Diagnostic[] = [];
  const cascaded = hasModules ? applyCascade(raw, cascadeDiags) : raw;
  // Validate the cascaded model so `required cascade` fields counted as
  // filled when an ancestor sets them — otherwise nested instances that
  // legitimately inherit `team` (and similar) trip REQUIRED_NOT_FILLED
  // even though cascade would have stamped a value.
  //
  // Validation runs over the NON-stubbed model on purpose (v0.6, §4.13): the
  // undefined references are exactly what should surface as `todo`. If we
  // validated the stubbed model the stubs would resolve and the TODOs would
  // vanish. Synthesis is a render concern, not a diagnostics one.
  const validation = validate(cascaded);

  return withStubs({
    model: cascaded,
    rawModel: raw,
    graph: hasModules ? buildGraph(cascaded) : EMPTY_GRAPH,
    diagnostics: [
      ...loaded.resolved.diagnostics,
      ...cascadeDiags,
      ...validation.diagnostics,
    ],
  });
}

/**
 * Augment a viewer state with stub modules/interfaces for process references to
 * not-yet-defined elements (v0.6, §4.13), so the skeleton draws — edges form,
 * stub nodes render dashed. Pure; returns the input unchanged when nothing is
 * missing. The diagnostics are left as-is: they were computed over the
 * non-stubbed model, so the undefined refs stay surfaced as `todo`.
 *
 * Shared by the cold `buildViewerState` and the incremental viewer so both
 * paths produce identical stubbed output. A process-only workspace (zero
 * authored modules) renders its full skeleton through here.
 */
export function withStubs(state: ViewerState): ViewerState {
  const { model } = synthesizeStubs(state.model);
  if (model === state.model) return state;
  return { ...state, model, graph: buildGraph(model) };
}
